const aiLanding = {
  title: "Artificial Intelligence",
  subtitle: "A personal knowledge base on AI — understanding the technology that is reshaping how we work, think, and build.",
  intro: [
    "I am a software engineer who has been watching AI evolve from a research curiosity into something that changes how I write code, make decisions, and think about the future. This is not an academic survey — it is one practitioner's attempt to make sense of a field that moves faster than anyone can keep up with.",
    "The goal here is not to hype or fear-monger. It is to understand the fundamentals well enough to use AI effectively today and think clearly about where it is going. Most of what matters is not the latest model release — it is the underlying ideas that keep showing up across decades of research.",
    "This section is where I collect what I have learned from papers, courses, building with AI tools, and conversations with people smarter than me. It is a living document — updated as my understanding deepens and the field evolves."
  ],
  howToUse: [
    {
      title: "Core Concepts",
      description: "The foundational ideas behind modern AI — neural networks, training, inference, and why they matter.",
      path: "/ai/concepts"
    },
    {
      title: "Large Language Models",
      description: "How LLMs work, what they can and cannot do, and how to use them effectively.",
      path: "/ai/llms"
    },
    {
      title: "AI Tools & Applications",
      description: "A practical guide to AI tools for coding, writing, research, and daily work.",
      path: "/ai/tools"
    }
  ],
  philosophy: [
    { text: "Understand the fundamentals before chasing the latest model. Transformers, attention, and embeddings are the ideas that keep compounding.", link: "/ai/concepts" },
    { text: "AI is a tool, not a replacement. The best results come from human judgement combined with AI capability.", link: "/ai/tools" },
    { text: "Prompt engineering is just clear thinking. If you cannot explain what you want to a person, you cannot explain it to an AI.", link: "/ai/llms" },
    { text: "Be skeptical of benchmarks. Real-world usefulness and benchmark scores are different things.", link: "/ai/llms" },
    { text: "Learn by building. Reading about AI is useful, but the real understanding comes from using it to solve actual problems.", link: "/ai/tools" },
    { text: "Privacy and safety are not afterthoughts. Think about what data you share and what decisions you delegate.", link: "/ai/concepts" },
    { text: "The best AI use cases are boring. Summarizing documents, writing tests, searching codebases — not replacing human creativity.", link: "/ai/tools" },
    { text: "Stay curious but grounded. The field moves fast, but the fundamentals change slowly." }
  ],
  promptSection: {
    title: "Prompt Engineering",
    description: "A practical guide to writing better prompts — structure, context, examples, and the patterns that consistently produce better results.",
    path: "/ai/llms"
  }
};

export default aiLanding;
